import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { OrganizationContext } from '../../../../components/contexts/organization';
import {
  CouponPackMasterQuery,
  useBulkIssueCouponsMutation,
  useBulkIssuedCouponsStatusLazyQuery,
} from '../../../../graphql/generated';

export type BulkCouponPackStartFormValues = {
  issueNumber: number;
  description: string;
};

export type UseCouponPackBulkStartUseCase = {
  loading: boolean;
  onSubmit: (form: BulkCouponPackStartFormValues) => void;
};

/**
 * TODO: Replace with real GraphQL mutations when API is ready
 * This is a mock implementation following the same pattern as useListCouponBulkStart
 */
export const useCouponPackBulkStart = (
  couponPackMaster?: CouponPackMasterQuery['couponPackMaster'],
  refetch?: () => void
): UseCouponPackBulkStartUseCase => {
  const { organization, loading: organizationLoading } = useContext(OrganizationContext);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // URLを取得するための処理
  const [getBulkIssuedCouponsStatus, bulkIssuedCouponStatusContext] = useBulkIssuedCouponsStatusLazyQuery({
    fetchPolicy: 'network-only',
    pollInterval: 1000,
  });

  const [bulkIssueCouponPacksMutation] = useBulkIssueCouponsMutation({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      getBulkIssuedCouponsStatus({
        variables: {
          executionArn: data.bulkIssueCoupons.executionArn,
        },
      });
    },
  });

  const onSubmit = useCallback(
    (form: BulkCouponPackStartFormValues) => {
      const start = async (form: BulkCouponPackStartFormValues): Promise<void> => {
        if (organization) {
          setLoading(true);
          try {
            await bulkIssueCouponPacksMutation({
              variables: {
                input: {
                  couponMasterId: couponPackMaster?.id || '',
                  issuedNumber: +form.issueNumber,
                  publishedOrganizationId: organization.id,
                  publishedOrganizationName: organization.name,
                  publishedFrom: 'admin',
                  description: form.description || '',
                  couponPack: true,
                },
              },
            });

            enqueueSnackbar('回数券URL一括発行処理を受付けました。しばらくお待ち下さい', { variant: 'info' });
          } catch (err) {
            setLoading(false);
            enqueueSnackbar('回数券の一括発行でエラーが発生しました', {
              variant: 'error',
            });
            Bugsnag.notify(err);
          }
        }
      };
      start(form);
    },
    [organization, couponPackMaster, enqueueSnackbar, refetch]
  );

  // 一括発行ステータスをポーリングする
  useEffect(() => {
    const status = bulkIssuedCouponStatusContext?.data?.bulkIssuedCouponsStatus?.status;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'ABORTED' || status === 'FAILED' || status === 'SUCCEEDED' || status === 'TIMED_OUT') {
      if (bulkIssuedCouponStatusContext && bulkIssuedCouponStatusContext.stopPolling) {
        bulkIssuedCouponStatusContext.stopPolling();
      }
    }

    // 正常に終了した場合は、リフェッチする。ファイル名を確定させるために履歴情報がほしい
    if (
      status === 'SUCCEEDED' &&
      bulkIssuedCouponStatusContext &&
      bulkIssuedCouponStatusContext.data &&
      bulkIssuedCouponStatusContext.data.bulkIssuedCouponsStatus &&
      bulkIssuedCouponStatusContext.data.bulkIssuedCouponsStatus.bucket &&
      bulkIssuedCouponStatusContext.data.bulkIssuedCouponsStatus.key
    ) {
      setLoading(false);
      enqueueSnackbar('処理が正常に終了しました', { variant: 'success' });
      if (refetch) {
        refetch();
      }
    } else if (status === 'FAILED' || status === 'TIMED_OUT') {
      setLoading(false);
      enqueueSnackbar('エラーが発生しました', {
        variant: 'error',
      });
      Bugsnag.notify(bulkIssuedCouponStatusContext.error || new Error('bulk coupon polling error'));
    }
  }, [bulkIssuedCouponStatusContext]);

  return {
    loading: loading || organizationLoading,
    onSubmit,
  };
};
